import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  TrendingUp,
  CheckCircle2,
  Activity,
  Wallet,
  ArrowRight,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import {
  getMerchantBalance,
  getQuickStats,
  getDashboardStats,
  type MerchantBalanceResponse,
  type QuickStatsResponse,
  type DashboardStatsResponse,
  type MerchantWallet,
} from "@/lib/api";

// Couleurs officielles opérateurs / cartes — même palette que liquid.
const METHOD_COLORS: Record<string, string> = {
  mpesa: "#00A551",
  orange: "#FF7A00",
  airtel: "#E4002B",
  afrimoney: "#F5B300",
  visa: "#1A1F71",
  mastercard: "#EB001B",
};

// Statuts POS (Pending → Successful | Failed)
const STATUS_COLORS: Record<string, string> = {
  Successful: "#10b981",
  Pending: "#f59e0b",
  Failed: "#ef4444",
};

const STATUS_ORDER = ["Successful", "Pending", "Failed"];

function formatMoneyStrict(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount.toLocaleString("fr-FR")} ${currency}`;
  }
}

function formatVolumes(byCurrency: Record<string, number>): string {
  const entries = Object.entries(byCurrency).filter(([, v]) => v > 0);
  if (entries.length === 0) return "—";
  return entries.map(([c, v]) => formatMoneyStrict(v, c)).join(" · ");
}

function operatorLabel(method: string): string {
  return (
    {
      mpesa: "M-Pesa",
      orange: "Orange Money",
      airtel: "Airtel Money",
      afrimoney: "AfriMoney",
      visa: "Visa",
      mastercard: "Mastercard",
    } as Record<string, string>
  )[method.toLowerCase()] ?? method;
}

// ---------------------------------------------------------------------------

export function DashboardPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const merchantId = auth.status === "authenticated" ? auth.user.merchant_id : null;
  const [days] = useState(7); // /analytics/dashboard/stats couvre 7 jours

  const balanceQuery = useQuery<MerchantBalanceResponse>({
    queryKey: ["merchant-balance", merchantId],
    queryFn: () => getMerchantBalance(merchantId as number),
    enabled: !!merchantId,
  });

  const quickQuery = useQuery<QuickStatsResponse>({
    queryKey: ["quick-stats", merchantId],
    queryFn: getQuickStats,
    enabled: !!merchantId,
  });

  const statsQuery = useQuery<DashboardStatsResponse>({
    queryKey: ["dashboard-stats", merchantId],
    queryFn: getDashboardStats,
    enabled: !!merchantId,
  });

  const isLoading = balanceQuery.isLoading || quickQuery.isLoading || statsQuery.isLoading;
  if (isLoading) {
    return <div className="card card-body text-[#7e8299]">Chargement…</div>;
  }

  // ----- Agrégats aujourd'hui (KPIs cards) -----
  const today = quickQuery.data?.today ?? [];
  const totalTx = quickQuery.data?.totals?.today_transactions ?? 0;
  const totalFailed = quickQuery.data?.totals?.today_failed ?? 0;
  const totalSuccess = totalTx - totalFailed;
  const successRate = totalTx > 0 ? (totalSuccess / totalTx) * 100 : 0;
  const todayVolumes: Record<string, number> = {};
  for (const b of today) {
    todayVolumes[b.currency] = (todayVolumes[b.currency] ?? 0) + Number(b.successful_volume ?? 0);
  }

  // ----- Répartition par méthode et par statut sur 7j (à partir de daily_transactions) -----
  const daily = statsQuery.data?.daily_transactions ?? [];

  // by_status (agrégé 7 jours, toutes devises)
  const byStatusMap: Record<string, number> = {};
  for (const row of daily) {
    byStatusMap[row.status] = (byStatusMap[row.status] ?? 0) + Number(row.transaction_count ?? 0);
  }
  const statusBarData = STATUS_ORDER.filter((s) => byStatusMap[s] !== undefined).map((s) => ({
    status: s,
    count: byStatusMap[s] ?? 0,
    fill: STATUS_COLORS[s] ?? "#64748b",
  }));

  // by_method — pas dispo directement dans /analytics/dashboard/stats.
  // On dérive depuis les wallets (nombre de wallets ≠ nombre de tx) puis on
  // remplace par les success_transactions si l'endpoint /operators/performance
  // sera branché en follow-up. Pour l'instant on affiche une répartition
  // "par wallet actif" comme signal visuel.
  const walletMethodCounts: Record<string, number> = {};
  for (const w of balanceQuery.data?.wallets ?? []) {
    walletMethodCounts[w.method] = (walletMethodCounts[w.method] ?? 0) + 1;
  }
  const totalMethodCount = Object.values(walletMethodCounts).reduce((a, b) => a + b, 0);
  const methodPieData = Object.entries(walletMethodCounts)
    .sort((a, b) => b[1] - a[1])
    .map(([method, count]) => ({
      name: method,
      value: count,
      fill: METHOD_COLORS[method.toLowerCase()] ?? "#64748b",
    }));

  // ----- Wallets regroupés par devise -----
  const wallets = balanceQuery.data?.wallets ?? [];
  const byCurrency: Record<string, MerchantWallet[]> = {};
  for (const w of wallets) {
    (byCurrency[w.currency] ??= []).push(w);
  }
  const currencies = Object.keys(byCurrency).sort();

  return (
    <div>
      <PageHeader
        title="Vue d'ensemble"
        breadcrumbs={[{ label: "Tableau de bord" }, { label: "Vue d'ensemble" }]}
        actions={
          <span className="text-xs text-slate-500">
            Période fixe : 7 derniers jours (endpoint POS)
          </span>
        }
      />

      {/* ---- 3 StatCards ---- */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        <StatCard
          label="Volume aujourd'hui"
          value={formatVolumes(todayVolumes)}
          sub={`${totalTx} paiement${totalTx > 1 ? "s" : ""}`}
          icon={<Activity size={20} />}
          color="primary"
        />
        <StatCard
          label="Paiements réussis"
          value={String(totalSuccess)}
          sub={`aujourd'hui · ${totalFailed} échec${totalFailed > 1 ? "s" : ""}`}
          icon={<CheckCircle2 size={20} />}
          color="success"
        />
        <StatCard
          label="Taux de succès"
          value={`${successRate.toFixed(1)}%`}
          sub={`sur ${totalTx} tentative${totalTx > 1 ? "s" : ""}`}
          icon={<TrendingUp size={20} />}
          color="info"
        />
      </div>

      {/* ---- Balance cards (2 max) ---- */}
      {currencies.length > 0 && (
        <div
          className="grid grid-cols-1 gap-4 mb-6"
          style={{ gridTemplateColumns: `repeat(${Math.min(currencies.length, 2)}, minmax(0, 1fr))` }}
        >
          {currencies.slice(0, 2).map((currency) => {
            const walletsForCurrency = byCurrency[currency];
            const total = walletsForCurrency.reduce((a, w) => a + Number(w.balance ?? 0), 0);
            return (
              <BalanceCard
                key={currency}
                currency={currency}
                total={total}
                byOperator={walletsForCurrency}
                onSeeHistory={() => navigate("/historique-de-transactions")}
              />
            );
          })}
        </div>
      )}

      {/* ---- 2 charts : donut + bar ---- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
        {/* Répartition par méthode */}
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">Wallets par méthode</div>
              <div className="card-subtitle">Répartition des wallets actifs</div>
            </div>
          </div>
          <div className="card-body">
            {methodPieData.length === 0 ? (
              <div className="text-center text-[#7e8299] py-8 text-sm">
                Aucun wallet configuré
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
                <div style={{ width: "100%", height: 220 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={methodPieData}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={55}
                        outerRadius={85}
                        paddingAngle={2}
                        stroke="none"
                      >
                        {methodPieData.map((entry) => (
                          <Cell key={entry.name} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value, _n, item) => {
                          const num = Number(value ?? 0);
                          const total = totalMethodCount || 1;
                          const pct = ((num / total) * 100).toFixed(1);
                          return [
                            `${num} wallet(s) (${pct}%)`,
                            (item as { payload?: { name?: string } })?.payload?.name ?? "",
                          ];
                        }}
                        contentStyle={{ borderRadius: 8, fontSize: 12 }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-2">
                  {methodPieData.map((m) => {
                    const pct = totalMethodCount > 0 ? (m.value / totalMethodCount) * 100 : 0;
                    return (
                      <div key={m.name} className="flex items-center gap-2 text-sm">
                        <span
                          className="w-3 h-3 rounded-full shrink-0"
                          style={{ background: m.fill }}
                        />
                        <span className="capitalize font-semibold flex-1">{m.name}</span>
                        <span className="text-[#7e8299] text-xs">
                          {m.value} · {pct.toFixed(0)}%
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Funnel par statut */}
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">Funnel par statut</div>
              <div className="card-subtitle">Toutes les tentatives · {days} jours</div>
            </div>
          </div>
          <div className="card-body">
            {statusBarData.length === 0 ? (
              <div className="text-center text-[#7e8299] py-8 text-sm">Aucune tentative</div>
            ) : (
              <>
                <div style={{ width: "100%", height: 200 }}>
                  <ResponsiveContainer>
                    <BarChart
                      data={statusBarData}
                      layout="vertical"
                      margin={{ top: 4, right: 16, left: 4, bottom: 4 }}
                    >
                      <XAxis type="number" allowDecimals={false} stroke="#94a3b8" fontSize={11} />
                      <YAxis
                        type="category"
                        dataKey="status"
                        stroke="#475569"
                        fontSize={12}
                        width={90}
                      />
                      <Tooltip
                        cursor={{ fill: "rgba(148,163,184,0.08)" }}
                        formatter={(value) => [`${Number(value ?? 0)} tx`, "Nombre"]}
                        contentStyle={{ borderRadius: 8, fontSize: 12 }}
                      />
                      <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                        {statusBarData.map((entry) => (
                          <Cell key={entry.status} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {statusBarData
                    .slice()
                    .sort((a, b) => b.count - a.count)
                    .map((row) => (
                      <div
                        key={row.status}
                        className="flex items-center justify-between p-2 rounded-lg bg-[#fcfcfd] border border-[#eff2f5]"
                      >
                        <span
                          className="text-xs font-semibold px-2 py-0.5 rounded"
                          style={{
                            background: row.fill + "20",
                            color: row.fill,
                          }}
                        >
                          {row.status}
                        </span>
                        <div className="text-right">
                          <div className="font-bold text-[#181c32] text-sm leading-none">
                            {row.count}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ---- Recent payments (placeholder link vers page transactions à venir) ---- */}
      <div className="card">
        <div className="card-header">
          <div>
            <div className="card-title">Paiements récents</div>
            <div className="card-subtitle">L'historique complet sera disponible sur la page Transactions.</div>
          </div>
          <button
            onClick={() => navigate("/historique-de-transactions")}
            className="btn btn-light btn-sm"
          >
            Voir tous
            <ArrowRight size={14} />
          </button>
        </div>
        <div className="px-6 py-12 text-center text-sm text-[#7e8299]">
          La liste des transactions récentes sera branchée lors du portage de la page
          <strong className="text-[#3f4254] mx-1">/historique-de-transactions</strong>
          (Phase 2 · tâche #9).
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function StatCard({
  label,
  value,
  sub,
  icon,
  color,
}: {
  label: string;
  value: string;
  sub?: string;
  icon: React.ReactNode;
  color: "primary" | "success" | "info" | "warning";
}) {
  const cls: Record<typeof color, string> = {
    primary: "bg-brand-50 text-brand-700",
    success: "bg-success-50 text-success-700",
    info: "bg-info-50 text-info-700",
    warning: "bg-warning-50 text-warning-700",
  };
  return (
    <div className="card card-body">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wider text-[#7e8299] font-bold">{label}</div>
          <div className="text-2xl font-bold text-[#181c32] mt-2 truncate">{value}</div>
          {sub && <div className="text-xs text-[#7e8299] mt-1 truncate">{sub}</div>}
        </div>
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${cls[color]}`}>
          {icon}
        </div>
      </div>
    </div>
  );
}

function BalanceCard({
  currency,
  total,
  byOperator,
  onSeeHistory,
}: {
  currency: string;
  total: number;
  byOperator: MerchantWallet[];
  onSeeHistory?: () => void;
}) {
  const [showDetail, setShowDetail] = useState(false);
  const gradient =
    currency === "USD"
      ? "from-emerald-500 to-emerald-700"
      : currency === "CDF"
      ? "from-sky-500 to-sky-700"
      : "from-slate-500 to-slate-700";

  return (
    <div className={`card overflow-hidden relative bg-gradient-to-br ${gradient} text-left`}>
      <div className="px-6 py-5 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Wallet size={16} className="opacity-90" />
            <div className="text-sm opacity-90">Solde global {currency}</div>
          </div>
          <span className="text-[10px] font-bold tracking-wider bg-white/20 rounded px-2 py-0.5">
            {currency}
          </span>
        </div>
        <div className="text-3xl font-bold mt-2 truncate">{formatMoneyStrict(total, currency)}</div>
        <div className="text-xs opacity-80 mt-1">
          {byOperator.length} wallet{byOperator.length > 1 ? "s" : ""} sur cette devise
        </div>

        {byOperator.length > 0 && (
          <button
            type="button"
            onClick={() => setShowDetail((v) => !v)}
            className="mt-3 text-[11px] uppercase tracking-wider opacity-90 hover:opacity-100 flex items-center gap-1 font-semibold"
          >
            {showDetail ? "Masquer" : "Détail par opérateur"}
            {showDetail ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
        )}

        {showDetail && byOperator.length > 0 && (
          <div className="mt-3 pt-3 border-t border-white/20 space-y-1.5">
            {byOperator.map((op) => (
              <div key={op.wallet_id} className="flex items-center justify-between text-xs">
                <span className="capitalize opacity-95 font-medium">{operatorLabel(op.method)}</span>
                <span className="font-bold tabular-nums">
                  {formatMoneyStrict(Number(op.balance ?? 0), currency)}
                </span>
              </div>
            ))}
          </div>
        )}

        <button
          type="button"
          onClick={onSeeHistory}
          className="mt-3 text-[10px] uppercase tracking-wider opacity-70 hover:opacity-100 flex items-center gap-1"
        >
          Voir l'historique <ArrowRight size={10} />
        </button>
      </div>
    </div>
  );
}
